import clsx from 'clsx'
import { useEffect, useRef, useState, type HTMLAttributes } from 'react'
import 'tailwindcss/tailwind.css'
import SmoothImage from './smooth-image'

type FaderProps = HTMLAttributes<HTMLDivElement> & {
    urls: string[]
    interval?: number
}

function Fader(props: FaderProps): JSX.Element {
    const { urls, interval = 10000, className, children, ...otherProps } = props
    const timerRef = useRef<ReturnType<typeof setInterval>>()
    const [currentImageIndex, setCurrentImageIndex] = useState<number | undefined>(urls.length > 0 ? 0 : undefined)

    useEffect(() => {
        if (urls.length > 1) {
            timerRef.current = setInterval(() => {
                setCurrentImageIndex((state) => {
                    if (state === undefined) return 0
                    const nextState = state + 1
                    return nextState >= urls.length ? 0 : nextState
                })
            }, interval)
        }

        return () => {
            clearInterval(timerRef.current)
        }
    }, [urls, interval])

    return (
        <div className={clsx('relative h-full w-full bg-neutral-950', className)} {...otherProps}>
            {urls.map((url, index) => (
                <SmoothImage
                    loading={index === 0 ? 'eager' : 'lazy'}
                    fadeIn={index === 0}
                    src={url}
                    alt="Background image"
                    width={1920}
                    height={1080}
                    key={url}
                    className={clsx(
                        'absolute inset-0 h-full w-full object-cover object-center transition-opacity duration-1000 ease-in-out',
                        index === currentImageIndex ? 'opacity-100' : 'opacity-0',
                    )}
                />
            ))}
            <div className="absolute inset-0 bg-neutral-950 opacity-75" />
            <div className="z-10 flex h-full w-full items-center justify-center">{children}</div>
        </div>
    )
}

export { Fader }
